import React from 'react';
import {Star} from 'lucide-react';
import {BillingSubscriptionSummary} from '../../../../services/billingApi';
import {SubscriptionPlan} from '../constants';
import styles from '../Settings.module.css';

interface SettingsSubscriptionSectionProps {
  currentPlan: SubscriptionPlan;
  subscriptionSummary?: BillingSubscriptionSummary | null;
  manageLoading?: boolean;
  onUpgrade: () => void;
  onManageSubscription: () => void;
}

const subscriptionStarIconStyle = {
  '--settings-subscription-star-url': `url("${process.env.PUBLIC_URL}/icons/star-filled.svg")`,
} as React.CSSProperties;

const benefitCheckIconStyle = {
  '--settings-benefit-check-url': `url("${process.env.PUBLIC_URL}/icons/check-o.svg")`,
} as React.CSSProperties;

const subscriptionManageIconStyle = {
  '--settings-subscription-manage-icon-url': `url("${process.env.PUBLIC_URL}/icons/bookmark-manager-outline.svg")`,
} as React.CSSProperties;

const formatBillingDate = (value?: string | null) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(parsedDate);
};

const SettingsSubscriptionSection: React.FC<SettingsSubscriptionSectionProps> = ({
  currentPlan,
  subscriptionSummary,
  manageLoading = false,
  onUpgrade,
  onManageSubscription,
}) => {
  const isFreePlan = currentPlan === 'free';
  const trialEndsAt = formatBillingDate(subscriptionSummary?.trialEndsAt);
  const currentPeriodEnd = formatBillingDate(subscriptionSummary?.currentPeriodEnd);
  const features = isFreePlan
    ? ['Unlimited posts', 'Service marketplace', '5 GB storage', '100 messages/month']
    : ['Unlimited posts & reels', 'HD video (1080p)', '50 GB storage', 'Advanced analytics', 'Ad-free experience', 'Priority support', 'Live streaming'];

  let billingMetaText = 'You have access to all premium features.';
  if (!isFreePlan && subscriptionSummary?.cancelAtPeriodEnd && currentPeriodEnd) {
    billingMetaText = `Your Pro plan will end on ${currentPeriodEnd}.`;
  } else if (!isFreePlan && trialEndsAt) {
    billingMetaText = `Your Pro trial is active through ${trialEndsAt}.`;
  } else if (!isFreePlan && currentPeriodEnd) {
    billingMetaText = `Your Pro plan renews on ${currentPeriodEnd}.`;
  }

  return (
    <section className={`${styles.settingsCard} ${styles.subscriptionCard} ${isFreePlan ? styles.subscriptionCardFree : styles.subscriptionCardPro}`}>
      <div className={styles.subscriptionHeader}>
        <div className={styles.subscriptionIdentity}>
          <span className={styles.subscriptionIcon}>
            <span className={styles.subscriptionStarAsset} style={subscriptionStarIconStyle} aria-hidden="true" />
          </span>
          <div>
            <div className={styles.subscriptionTitleRow}>
              <h3 className={styles.subscriptionHeading}>{isFreePlan ? 'XPSR Free' : 'XPSR Pro'}</h3>
              {!isFreePlan ? (
                <img
                  src={`${process.env.PUBLIC_URL}/xpsr_pro_badge_light.png`}
                  alt="XPSR Pro"
                  className={styles.subscriptionProBadge}
                />
              ) : null}
            </div>
            <p className={styles.subscriptionText}>{isFreePlan ? 'Upgrade to unlock premium features.' : billingMetaText}</p>
          </div>
        </div>

        {isFreePlan ? (
          <button type="button" onClick={onUpgrade} className={styles.subscriptionUpgradeButton}>
            <Star size={17} aria-hidden="true" /> Upgrade to Pro
          </button>
        ) : subscriptionSummary?.managementUrlAvailable ? (
          <button
            type="button"
            onClick={onManageSubscription}
            className={styles.subscriptionUpgradeButton}
            disabled={manageLoading}
          >
            <span className={styles.subscriptionManageIcon} style={subscriptionManageIconStyle} aria-hidden="true" />
            {manageLoading ? 'Opening...' : 'Manage Subscription'}
          </button>
        ) : null}
      </div>

      <div className={styles.planFeaturesPanel}>
        <h4 className={styles.planFeaturesTitle}>{isFreePlan ? 'Current Features' : 'Your Pro Benefits'}</h4>
        <div className={styles.planFeaturesGrid}>
          {features.map((feature) => (
            <div key={feature}>
              <span className={styles.benefitCheckIcon} style={benefitCheckIconStyle} aria-hidden="true" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default React.memo(SettingsSubscriptionSection);
